import { ButtonType, CheckBoxWithLabelWidget, DialogFooterButtonsWidget, DialogPositioningBehavior, DialogType } from '@tableau/widgets';
import * as React from 'react';
import { Messages } from '../../codegen/Localize';
import { angularProviderHOC } from '../../common/react/AngularProviderHOC';
import { SettingsFormService } from '../../settings/directives/tbSettingsCoordinator';
import { ISiteAdminSettings } from '../../settings/types/ISiteSettingsForSiteAdmin';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { DomainFamily } from '../../ts/ServerApi/types/IServerSettingsUnauthenticated';
import { ReactModalService } from '../../ts/services/ReactModalService';
import { ServerService } from '../../ts/services/ServerService';
import { appStore } from '../AppStore';
import { SessionServerSelectors, SessionSiteSelectors } from '../selectors/SessionInfoSelectors';
import { ServiceLocator } from '../ServiceLocator';
import { SiteSettingsStyles } from './styles/SiteSettingsStyles';

interface RunNowEnabledSiteSettingAngularProps {
    $q: ng.IQService;
    serverService: ServerService;
    formService: SettingsFormService;
}

interface RunNowEnabledSiteSettingOwnProps {
    settings: {
        siteAdminSettings: ISiteAdminSettings;
    };
}

interface RunNowEnabledSiteSettingState {
    isEnabled: boolean;
}

export type RunNowEnabledSiteSettingProps = RunNowEnabledSiteSettingOwnProps
    & RunNowEnabledSiteSettingAngularProps;

export class RunNowEnabledSiteSetting extends React.Component<RunNowEnabledSiteSettingProps, RunNowEnabledSiteSettingState> {
    private shouldRender: boolean;

    constructor(props: RunNowEnabledSiteSettingProps) {
        super(props);

        // We only render the option in certain circumstances
        const isServerAdmin = this.props.serverService.isSessionServerAdmin();
        const isOnline = this.props.serverService.isOnlineEnvironment();
        const isSiteAdmin = this.props.serverService.isSessionSiteAdmin();
        const isAllowedToChangeRunNowSetting = isServerAdmin
            || isOnline && isSiteAdmin;
        this.shouldRender = FeatureFlags.enableRunNowEnabled && isAllowedToChangeRunNowSetting;

        if (!this.shouldRender) {
            // If we don't render we don't register as part of the form or set any state
            return;
        }
        this.state = {
            isEnabled: SessionSiteSelectors.isRunNowEnabledOnSite(appStore.getState()),
        };

        // Gets the backend state and populates the component state with it
        this.openConfirmationDialog = this.openConfirmationDialog.bind(this);

        // Aggravatingly, we need to register with the form service in order to actually
        // revert when someone hits the revert button
        const formState = {
            formController: {
                $pristine: true,
                $dirty: false,
                $valid: true,
                $invalid: true,
                $submitted: false,
                $error: {},
                $addControl: () => { },
                $removeControl: () => { },
                $setValidity: () => { },
                $setDirty: () => { },
                $setPristine: () => { },
                $setSubmitted: () => { },
                $setUntouched: () => { },
                $commitViewValue: () => { },
                $rollbackViewValue: () => { },
            },
            cancel: () => {
                this.setState({
                    isEnabled: SessionSiteSelectors.isRunNowEnabledOnSite(appStore.getState()),
                });
            },
            // Annoyingly save is called after the update occurs
            // so we have to update the shared object that holds
            // the form state
            save: () => this.props.$q.resolve(),

            // "Optional" but actually required
            isBusy: () => false,
            isFormUnchanged: () => {
                return SessionSiteSelectors.isRunNowEnabledOnSite(appStore.getState()) === this.state.isEnabled;
            },
            isValid: () => true,
        };
        this.props.formService.registerFormStateByName(formState, 'Run Now Settings');
    }
    private openConfirmationDialog(): void {
        const confirmGlobalToggleTitle = this.state.isEnabled
            ? Messages.runNowDisable()
            : Messages.runNowEnable();
        const confirmGlobalToggleText = this.state.isEnabled
            ? Messages.runNowDisableSiteConfirmationText()
            : Messages.runNowEnableSiteConfirmationText();
        const modal = ReactModalService.open({
            title: confirmGlobalToggleTitle,
            testId: 'globally-toggle-runnow-dialog',
            type: DialogType.NormalWithTitleBar,
            positioningBehavior: DialogPositioningBehavior.Centered,
            dialogContent: confirmGlobalToggleText,
            footerContent: <DialogFooterButtonsWidget
                buttons={[
                    {
                        handleClick: () => {
                            modal.close();
                        },
                        buttonType: ButtonType.Grey,
                        children: Messages.runNowDialogCancel(),
                        testId: 'footer-cancel'
                    }, {
                        handleClick: () => {
                            const runNowEnabled = !this.state.isEnabled;
                            // We actually update the underlying form state by modifying the props
                            // this is really icky, but that's the way it has to be with angular
                            this.props.settings.siteAdminSettings.runNowEnabled = runNowEnabled;

                            this.setState({
                                isEnabled: runNowEnabled
                            }, () => {
                                // We then have to tell angular to actually check that the form changed
                                const context = ServiceLocator.getAngularActionContext();
                                context.scope.$apply();
                            });
                            modal.close();
                        },
                        buttonType: ButtonType.Go,
                        children: Messages.runNowDialogYes(),
                        testId: 'footer-yes'
                    }
                ]}
                testId="footer"
            />
        });
    }

    render() {
        if (!this.shouldRender || !this.state) {
            return null;
        }
        return <>
            <div style={SiteSettingsStyles.HeaderStyle}>{Messages.runNowEnabledSettingHeader()}</div>
            <div style={SiteSettingsStyles.BodyStyle}>
                <p>{Messages.runNowEnabledSettingDescription()}</p>
                <CheckBoxWithLabelWidget
                    containerStyle={SiteSettingsStyles.CheckboxStyle}
                    checked={SessionServerSelectors.isRunNowEnabledOnServer(appStore.getState()) && this.state.isEnabled}
                    handleChange={this.openConfirmationDialog}
                    testId="runnow-enable"
                    label={Messages.runNowGlobalToggleDialogCheckboxLabel()}
                    // undefined server setting shouldn't stop us from interacting with the form
                    disabled={SessionServerSelectors.isRunNowEnabledOnServer(appStore.getState()) === false}
                />
            </div>
        </>;
    }
}

export namespace RunNowEnabledSiteSettingAngularProvider {
    function getAngularServicesWithServiceLocator(): RunNowEnabledSiteSettingProps {
        return {
            $q: ServiceLocator.get('$q'),
            serverService: ServiceLocator.get('ServerService'),
            formService: ServiceLocator.get('SettingsFormService')
        } as any;
    }
    function selectPropsFromAngularServices(angularServices: RunNowEnabledSiteSettingProps) {
        return angularServices;
    }

    export const HOC = angularProviderHOC<RunNowEnabledSiteSettingProps, RunNowEnabledSiteSettingProps, RunNowEnabledSiteSettingProps>({
        getAngularServicesWithServiceLocator,
        selectPropsFromAngularServices,
    });
}

export const RunNowEnabledSiteSettingAng = RunNowEnabledSiteSettingAngularProvider.HOC(RunNowEnabledSiteSetting);
